import { BASE_OWNER_URL } from "@shared/config/base-url";
import { LocalStorageKey } from "@shared/config/local-storage-key";

// 현재 열려있는 소켓 레지스트리 — 세션 무효 시 일괄 정리하기 위함(clearSession 에서 사용).
const openSockets = new Set<WebSocket>();

/**
 * 열려있는 모든 실시간 소켓을 닫는다.
 * 세션 무효(로그아웃/재발급 실패) 시 호출해 스테일 세션에서 소켓이 잔존하는 것을 막는다.
 */
export const closeAllWebSockets = () => {
    openSockets.forEach((socket) => {
        try {
            socket.close();
        } catch {}
    });
    openSockets.clear();
};

export const createRawWebSocket = (
    bikeId: string,
    {
        onOpen,
        onMessage,
        onError,
        onClose,
    }: {
        onOpen?: (socket: WebSocket) => void;
        onMessage?: (data: any) => void;
        onError?: (event: Event) => void;
        onClose?: (event: CloseEvent) => void;
    }
): WebSocket => {
    const wsBaseUrl = BASE_OWNER_URL.replace(/^http/, "ws");
    // const url = `${wsBaseUrl}/ws/web?bikeIds=${encodeURIComponent(bikeId)}`;
    const url = `${wsBaseUrl}/ws/web`;

    const raw = localStorage.getItem(LocalStorageKey.AUTHORIZATION) ?? "";
    const accessToken = raw.startsWith("Bearer ") ? raw.slice(7) : raw;

    // [D3] 빈/없는 토큰으로는 소켓을 만들지 않는다 — 인증 실패가 모호한 6초 연결 타임아웃으로
    // 둔갑하지 않도록 즉시 표면화한다. (유일 콜러 force-lock 이 try/catch→safeReject 로 처리)
    if (!accessToken) {
        throw new Error("Authorization 토큰이 없습니다. 로그인/토큰 저장을 확인하세요.");
    }

    const socket = new WebSocket(url, ["jwt", accessToken]);
    openSockets.add(socket);

    socket.onopen = () => {
        // console.log(" WS OPEN", { url, protocol: socket.protocol });
        onOpen?.(socket);
    };

    socket.onmessage = async (event) => {
        try {
            let rawData: any = event.data;

            if (rawData instanceof Blob) rawData = await rawData.text();
            if (rawData instanceof ArrayBuffer) {
                rawData = new TextDecoder().decode(rawData);
            }

            let payload: any = rawData;

            if (typeof rawData === "string") {
                // 1차 파싱
                try { payload = JSON.parse(rawData); } catch { payload = rawData; }
                // 2차 파싱(따옴표 2중 케이스 등)
                if (typeof payload === "string") {
                    try { payload = JSON.parse(payload); } catch { /* 그대로 둠 */ }
                }
            }

            // 최종 정규화: 항상 { type, payload }를 만족하도록
            if (typeof payload === "string") {
                payload = { type: payload, payload: null };
            } else if (payload && typeof payload === "object") {
                const t = (payload as any).type;
                const p = (payload as any).payload;
                payload = { type: t, payload: p ?? null };
            } else {
                payload = { type: undefined, payload: null };
            }
            onMessage?.(payload);
        } catch (err) {
            console.error("[WS:PARSE_ERROR]", err, event.data);
            onMessage?.({ type: undefined, payload: null });
        }
    };

    socket.onerror = (event) => {
        console.error(" WS ERROR:", event);
        onError?.(event);
    };

    socket.onclose = (event) => {
        openSockets.delete(socket);
        console.warn("️ WS CLOSE:", { code: event.code, reason: event.reason });
        onClose?.(event);
    };

    return socket;
};
